import React from "react";
import { Stack, Typography, Divider, Box, alpha } from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import SchoolIcon from "@mui/icons-material/School";

import FormAlert from "../components/common/FormAlert";
import FormTextField from "../components/common/FormTextField";
import SubmitButton from "../components/buttons/SubmitButton";
import { useAppDispatch } from "../redux/store";
import { setupInstitution } from "../redux/actions/appStatusActions";
import { appStatusActions } from "../redux/slices/appStatus";
import { cleanFormData } from "../helpers/formHelpers";
import { InstitutionSetupRequestDto } from "../shared/dtos/institution/InstitutionSetupRequestDto";

const addressSchema = Yup.object().shape({
  streetLine1: Yup.string().required("Street is required"),
  streetLine2: Yup.string().nullable(),
  city: Yup.string().required("City is required"),
  stateOrProvince: Yup.string().required("State is required"),
  postalCode: Yup.string().required("Postal code is required"),
  country: Yup.string().required("Country is required"),
});

const setupSchema = Yup.object().shape({
  schoolDetails: Yup.object().shape({
    name: Yup.string().required("School name is required"),
    registrationNumber: Yup.string().required(
      "Registration number is required",
    ),
    contactEmail: Yup.string()
      .email("Invalid email")
      .required("Email is required"),
    contactPhone: Yup.string().required("Phone is required"),
    vision: Yup.string().required("Vision is required"),
    mission: Yup.string().required("Mission is required"),
    motto: Yup.string().required("Motto is required"),
    website: Yup.string().url("Invalid URL").nullable(),
    logoUrl: Yup.string().url("Invalid URL").required("Logo URL is required"),
    headOfficeAddress: addressSchema,
    mainBranch: Yup.object().shape({
      name: Yup.string().required("Branch name is required"),
      branchCode: Yup.string().required("Branch code is required"),
      licenseKey: Yup.string().required("License key is required"),
      principalName: Yup.string().required("Principal name is required"),
      contactEmail: Yup.string()
        .email("Invalid email")
        .required("Email is required"),
      contactPhone: Yup.string().required("Phone is required"),
      address: addressSchema,
    }),
  }),
  adminFirstName: Yup.string().required("Admin first name is required"),
  adminLastName: Yup.string().required("Admin last name is required"),
  adminEmail: Yup.string()
    .email("Invalid email")
    .required("Admin email is required"),
  adminPassword: Yup.string()
    .min(6, "Min 6 characters")
    .required("Password is required"),
});

interface SetupInstitutionFormProps {
  loading: boolean;
  error: string | null;
  onSuccess: () => void;
}

const SetupInstitutionForm: React.FC<SetupInstitutionFormProps> = ({
  loading,
  error = null,
  onSuccess,
}) => {
  const dispatch = useAppDispatch();

  const initialValues: InstitutionSetupRequestDto = {
    schoolDetails: {
      name: "",
      registrationNumber: "",
      vision: "",
      mission: "",
      motto: "",
      website: "",
      logoUrl: "",
      contactEmail: "",
      contactPhone: "",
      headOfficeAddress: {
        streetLine1: "",
        streetLine2: "",
        city: "",
        stateOrProvince: "",
        postalCode: "",
        country: "Kenya",
      },
      mainBranch: {
        name: "Main Campus",
        branchCode: "MAIN",
        licenseKey: "",
        principalName: "",
        contactEmail: "",
        contactPhone: "",
        address: {
          streetLine1: "",
          streetLine2: "",
          city: "",
          stateOrProvince: "",
          postalCode: "",
          country: "Kenya",
        },
      },
    },
    adminFirstName: "",
    adminLastName: "",
    adminEmail: "",
    adminPassword: "",
  };

  return (
    <Box sx={{ width: "100%", maxHeight: "70vh", overflowY: "auto", pr: 1 }}>
      <FormAlert
        message={error}
        severity="error"
        closable
        onClose={() =>
          dispatch(appStatusActions.setErrorSetupInstitution(null))
        }
      />
      <Formik
        initialValues={initialValues}
        validationSchema={setupSchema}
        onSubmit={async (values) => {
          const payload = cleanFormData(values) as InstitutionSetupRequestDto;
          const success = await dispatch(setupInstitution(payload));
          if (success) onSuccess();
        }}
      >
        {({ dirty, isValid }: FormikProps<InstitutionSetupRequestDto>) => (
          <Form>
            <Stack spacing={3}>
              {/* 1. School Identity */}
              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 700, color: "primary.main", mb: 1 }}
                >
                  1. Institution Identity
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    gridTemplateColumns: { xs: "1fr", sm: "2fr 1fr" },
                  }}
                >
                  <FormTextField
                    name="schoolDetails.name"
                    label="Institution Legal Name"
                  />
                  <FormTextField
                    name="schoolDetails.registrationNumber"
                    label="Registration Number"
                  />
                </Box>
                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    mt: 2,
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  }}
                >
                  <FormTextField
                    name="schoolDetails.contactEmail"
                    label="Official Email"
                    type="email"
                  />
                  <FormTextField
                    name="schoolDetails.contactPhone"
                    label="Official Telephone"
                  />
                </Box>
                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    mt: 2,
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  }}
                >
                  <FormTextField
                    name="schoolDetails.website"
                    label="Website URL (Optional)"
                  />
                  <FormTextField
                    name="schoolDetails.logoUrl"
                    label="Logo URL (CDN/Link)"
                  />
                </Box>
                <Box sx={{ mt: 2 }}>
                  <FormTextField
                    name="schoolDetails.motto"
                    label="School Motto"
                  />
                </Box>
                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    mt: 2,
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  }}
                >
                  <FormTextField
                    name="schoolDetails.vision"
                    label="Vision Statement"
                    multiline
                    rows={2}
                  />
                  <FormTextField
                    name="schoolDetails.mission"
                    label="Mission Statement"
                    multiline
                    rows={2}
                  />
                </Box>
              </Box>

              {/* Head Office Address */}
              <Box
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  bgcolor: (t) => alpha(t.palette.background.default, 0.6),
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, mb: 1.5 }}
                >
                  Head Office Physical Location
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  }}
                >
                  <FormTextField
                    name="schoolDetails.headOfficeAddress.streetLine1"
                    label="Street Address"
                  />
                  <FormTextField
                    name="schoolDetails.headOfficeAddress.city"
                    label="City"
                  />
                  <FormTextField
                    name="schoolDetails.headOfficeAddress.stateOrProvince"
                    label="State/County"
                  />
                  <FormTextField
                    name="schoolDetails.headOfficeAddress.postalCode"
                    label="Postal Code"
                  />
                </Box>
              </Box>

              <Divider />

              {/* 2. Main Campus */}
              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 700, color: "secondary.main", mb: 1 }}
                >
                  2. Primary Branch & Software License
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    gridTemplateColumns: { xs: "1fr", sm: "2fr 1fr 1fr" },
                  }}
                >
                  <FormTextField
                    name="schoolDetails.mainBranch.name"
                    label="Branch Name"
                  />
                  <FormTextField
                    name="schoolDetails.mainBranch.branchCode"
                    label="Code"
                  />
                  <FormTextField
                    name="schoolDetails.mainBranch.licenseKey"
                    label="Activation License Key"
                  />
                </Box>
                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    mt: 2,
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                  }}
                >
                  <FormTextField
                    name="schoolDetails.mainBranch.principalName"
                    label="Headteacher/Principal"
                  />
                  <FormTextField
                    name="schoolDetails.mainBranch.contactEmail"
                    label="Branch Email"
                    type="email"
                  />
                  <FormTextField
                    name="schoolDetails.mainBranch.contactPhone"
                    label="Branch Phone"
                  />
                </Box>
              </Box>

              <Divider />

              {/* 3. System Administrator */}
              <Box
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  bgcolor: (t) => alpha(t.palette.background.default, 0.6),
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, mb: 1.5 }}
                >
                  Master System Administrator Account
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  }}
                >
                  <FormTextField name="adminFirstName" label="First Name" />
                  <FormTextField name="adminLastName" label="Last Name" />
                  <FormTextField
                    name="adminEmail"
                    label="Super Admin Email"
                    type="email"
                  />
                  <FormTextField
                    name="adminPassword"
                    label="Secure Password"
                    type="password"
                  />
                </Box>
              </Box>

              <SubmitButton
                title="Initialize Institution System"
                loading={loading}
                loadingText="Initializing..."
                disabled={!dirty || !isValid || loading}
                startIcon={<SchoolIcon />}
                fullWidth
                sx={{ py: 1.8, fontWeight: 700 }}
              />
            </Stack>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default SetupInstitutionForm;
